import {
  useListExceptionGroups,
  useListExceptions,
} from '@/generated/hooks/astral/v1/api';
import type { V1Exception } from '@/generated/hooks/astral/models';
import { useQueries } from '@tanstack/react-query';

import {
  getGetRunQueryOptions,
  useListRuns,
} from '@/generated/hooks/jeeves/v1/api';
import {
  V1InvocationSource,
  type V1Run,
} from '@/generated/hooks/jeeves/models';
import type { TAssetActivityTone } from '@/layouts/AssetWorkspaceLayout';
import { RUN_STAGE } from '@/shared/constants';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder, parseResourceName } from '@/shared/utils';

/** A run's start never changes, so its fetch can stay fresh a while. */
const RUN_START_STALE_TIME_MS = 5 * 60 * 1000;

/** Exceptions shown in "Waiting on you"; the count still covers every one. */
const PENDING_EXCEPTIONS_PAGE_SIZE = 20;

/** Exception `stage` filter values: 1 is draft, 2 is published. */
const EXCEPTION_STAGE = { draft: 1, published: 2 } as const;

/** What is waiting on the user for one automation, and its latest run. */
export interface IAutomationActivitySummary {
  pendingExceptions: V1Exception[];
  /** Every pending exception, not only the ones fetched. */
  pendingCount: number;
  /** When each pending exception's run started, by run id, as runs load. */
  runStartTimes: ReadonlyMap<string, string>;
  /** Runs, by id, whose start is still loading. */
  loadingRunIds: ReadonlySet<string>;
  /**
   * The readable title of each exception group, by the exception's `group`.
   * An exception's `message` is a raw traceback, so the group names it instead.
   */
  groupTitles: ReadonlyMap<string, string>;
  latestRun?: V1Run;
  isLoading: boolean;
}

/**
 * The tone of a run's status dot in the activity bar.
 *
 * @param run - The run
 * @returns Its tone, or undefined for a run with no state yet
 */
export function getRunActivityTone(
  run: V1Run | undefined,
): TAssetActivityTone | undefined {
  const state = run?.state;
  if (!state) return undefined;
  if (state.completed) return 'success';
  if (state.failed || state.stopped) return 'error';
  if (state.awaiting_guidance) return 'attention';
  return 'running';
}

/**
 * The pending exceptions and latest run of one automation at one stage, for
 * its workspace header and the "Waiting on you" banner of its Activity view.
 *
 * @param automationId - The automation
 * @param isDraft - Draft runs and exceptions, else published ones
 * @param options - `withRunStartTimes` also loads each pending exception's run,
 *   for the banner's rows
 * @returns The summary
 */
export function useAutomationActivitySummary(
  automationId: string,
  isDraft: boolean,
  { withRunStartTimes = false }: { withRunStartTimes?: boolean } = {},
): IAutomationActivitySummary {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const enabled = Boolean(automationId && organization && workspace);

  const exceptionsQuery = useListExceptions(
    organization,
    workspace,
    {
      filter: FilterBuilder.create()
        .equals('automation', automationId)
        .equals('state', 'PENDING')
        .equals(
          'stage',
          isDraft ? EXCEPTION_STAGE.draft : EXCEPTION_STAGE.published,
        )
        .build(),
      page_size: PENDING_EXCEPTIONS_PAGE_SIZE,
    },
    { query: { enabled, retry: false } },
  );

  const runsQuery = useListRuns(
    organization,
    workspace,
    automationId,
    {
      page_size: 1,
      filter: FilterBuilder.create()
        .equals('archived', 'false')
        .equals('stage', isDraft ? RUN_STAGE.DRAFT : RUN_STAGE.PUBLISHED)
        .notEquals(
          'invocation_source',
          V1InvocationSource.INVOCATION_SOURCE_TEST,
        )
        .build(),
      order_by: 'create_time desc',
    },
    { query: { enabled, retry: false } },
  );

  const pendingExceptions = exceptionsQuery.data?.data.exceptions ?? [];
  // A run is what the user recognises a pending exception by, so each row is
  // named by its run's start; the exception only carries the run's id.
  const pendingRunIds = [
    ...new Set(
      pendingExceptions
        .map(exception => parseResourceName(exception.run).run ?? exception.run)
        .filter(Boolean),
    ),
  ];
  const runQueries = useQueries({
    queries: pendingRunIds.map(runId =>
      getGetRunQueryOptions(organization, workspace, automationId, runId, {
        query: {
          enabled: enabled && withRunStartTimes,
          staleTime: RUN_START_STALE_TIME_MS,
        },
      }),
    ),
  });
  const groupsQuery = useListExceptionGroups(
    organization,
    workspace,
    {},
    { query: { enabled: enabled && withRunStartTimes } },
  );
  const groupTitles = new Map<string, string>();
  for (const group of groupsQuery.data?.data.groups ?? []) {
    if (!group.name || !group.title) continue;
    // Matched by full name or by id, whichever form the exception carries.
    groupTitles.set(group.name, group.title);
    const id = group.name.split('/').pop();
    if (id) groupTitles.set(id, group.title);
  }

  const runStartTimes = new Map<string, string>();
  const loadingRunIds = new Set<string>();
  runQueries.forEach((query, index) => {
    const startTime = query.data?.data.create_time;
    if (startTime) runStartTimes.set(pendingRunIds[index], startTime);
    if (query.isLoading) loadingRunIds.add(pendingRunIds[index]);
  });

  return {
    pendingExceptions,
    runStartTimes,
    loadingRunIds,
    groupTitles,
    pendingCount:
      exceptionsQuery.data?.data.total_size ?? pendingExceptions.length,
    latestRun: runsQuery.data?.data.runs?.[0],
    isLoading: exceptionsQuery.isLoading || runsQuery.isLoading,
  };
}
